/**
 * SaaS beauty product modules shown on the salon hub.
 * Entitlement keys can map to DB module names / ny_salon_modules rows.
 */

export const SAAS_MODULE_IDS = {
  VIRTUAL_MAKEUP: 'virtual_makeup',
  HAIR_COLORING: 'hair_coloring',
  HAIR_CUT: 'hair_cut',
  HAIR_ANALYZE: 'hair_analyze',
  HAIR_STYLING: 'hair_styling',
};

/** Legacy combined SKU — expands to all three beauty studio modules. */
export const LEGACY_MAKEUP_HAIR_MODULE_IDS = [
  'makeup-hair',
  'makeup_hair',
  'Makeup Analyzer',
  'makeup analyzer',
  'virtual_makeup_hair',
];

export const BEAUTY_SERVICES = {
  MAKEUP: 'makeup',
  HAIR_COLORING: 'hair-coloring',
  HAIR_CUT: 'hair-cut',
};

/**
 * @typedef {object} BeautyProductConfig
 * @property {string} moduleId
 * @property {string} service  Studio selectedService lock
 * @property {string} appBase  Route prefix (no trailing slash)
 * @property {string} hubPath  Back-to-hub path
 * @property {string} title
 * @property {string} description
 * @property {string} cta
 * @property {string} [enterpriseAppBase]
 * @property {string} [enterpriseHubPath]
 */

/** @type {Record<string, BeautyProductConfig>} */
export const BEAUTY_PRODUCTS = {
  makeup: {
    moduleId: SAAS_MODULE_IDS.VIRTUAL_MAKEUP,
    service: BEAUTY_SERVICES.MAKEUP,
    appBase: '/makeup',
    hubPath: '/salon/hub',
    enterpriseAppBase: '/enterprise/makeup-studio-test',
    enterpriseHubPath: '/enterprise/salon/test',
    title: 'Virtual Makeup',
    description:
      'Capture your face, pick a named look, and apply the full makeup preset in one tap.',
    cta: 'Open Looks Studio',
  },
  /** Enterprise preset-first makeup flow (same entitlement as virtual_makeup). */
  looks: {
    moduleId: SAAS_MODULE_IDS.VIRTUAL_MAKEUP,
    service: BEAUTY_SERVICES.MAKEUP,
    appBase: '/makeup',
    hubPath: '/salon/hub',
    enterpriseAppBase: '/enterprise/makeup-studio-test',
    enterpriseHubPath: '/enterprise/salon/test',
    title: 'Makeup Looks',
    description:
      'Capture your face, choose a recommended or custom look, and apply full makeup in one tap.',
    cta: 'Continue',
  },
  hairColoring: {
    moduleId: SAAS_MODULE_IDS.HAIR_COLORING,
    service: BEAUTY_SERVICES.HAIR_COLORING,
    appBase: '/hair-coloring',
    naturalsAppBase: '/naturals/hair-coloring',
    naturalsHubPath: '/naturals/presentation',
    hubPath: '/salon/hub',
    enterpriseAppBase: '/enterprise/hair-coloring',
    enterpriseHubPath: '/enterprise/salon/test',
    title: 'Hair Coloring',
    description:
      'Try salon hair colour — shades, partial highlights, balayage, and custom masks with live compare.',
    cta: 'Open Hair Coloring',
  },
  hairCut: {
    moduleId: SAAS_MODULE_IDS.HAIR_CUT,
    service: BEAUTY_SERVICES.HAIR_CUT,
    appBase: '/hair-coloring',
    hubPath: '/salon/hub',
    enterpriseAppBase: '/enterprise/haircut',
    enterpriseHubPath: '/enterprise/salon/test',
    title: 'Hair Style',
    description:
      'AI hair try-on — pick a style and preview front, side, and back views.',
    cta: 'Open Hair Style',
  },
  hairStyling: {
    moduleId: SAAS_MODULE_IDS.HAIR_STYLING,
    service: 'hair-styling',
    appBase: '/enterprise/hair-styling',
    naturalsAppBase: '/naturals/hair-styling',
    naturalsHubPath: '/naturals/presentation',
    hubPath: '/salon/hub',
    enterpriseAppBase: '/enterprise/hair-styling',
    enterpriseHubPath: '/enterprise/salon/test',
    title: 'Hair Styling',
    description:
      'Hairstyle try-on — change cut and styling while keeping the face and original hair colour.',
    cta: 'Open Hair Styling',
  },
  makeupStudioTest: {
    moduleId: SAAS_MODULE_IDS.VIRTUAL_MAKEUP,
    service: 'makeup-flux-prompt-v3',
    appBase: '/enterprise/makeup-studio-test',
    naturalsAppBase: '/naturals/makeup-studio-test',
    naturalsHubPath: '/naturals/presentation',
    hubPath: '/salon/hub',
    enterpriseAppBase: '/enterprise/makeup-studio-test',
    enterpriseHubPath: '/enterprise/salon/test',
    title: 'Makeup Studio',
    description:
      'Capture your face, pick a time-of-use preset or custom colors, then apply your full look.',
    cta: 'Continue to studio',
    /** Salon test hub only — no cross-studio hairstyle / hair-color handoff. */
    hideLookHandoff: true,
  },
  /** Legacy path — redirects to Makeup Studio (v3). */
  makeupFluxLab: {
    moduleId: SAAS_MODULE_IDS.VIRTUAL_MAKEUP,
    service: 'makeup-flux-prompt-v3',
    appBase: '/enterprise/makeup-studio-test',
    hubPath: '/salon/hub',
    enterpriseAppBase: '/enterprise/makeup-studio-test',
    enterpriseHubPath: '/enterprise/salon/test',
    title: 'Makeup Studio',
    description: 'Merged into Makeup Studio — presets and custom colors.',
    cta: 'Open Makeup Studio',
    hideLookHandoff: true,
  },
  makeupFluxPromptV3: {
    moduleId: SAAS_MODULE_IDS.VIRTUAL_MAKEUP,
    service: 'makeup-flux-prompt-v3',
    appBase: '/enterprise/makeup-studio-test',
    hubPath: '/salon/hub',
    enterpriseAppBase: '/enterprise/makeup-studio-test',
    enterpriseHubPath: '/enterprise/salon/test',
    title: 'Makeup Studio',
    description: 'Same as Makeup Studio — time-of-use presets and custom colors.',
    cta: 'Open Makeup Studio',
    hideLookHandoff: true,
  },
  /** Prompt-only hairstyle FLUX — no catalog, masks, or composite. */
  hairFluxPrompt: {
    moduleId: SAAS_MODULE_IDS.HAIR_STYLING,
    service: 'hair-flux-prompt',
    appBase: '/enterprise/hair-flux-prompt',
    hubPath: '/salon/hub',
    enterpriseAppBase: '/enterprise/hair-flux-prompt',
    enterpriseHubPath: '/enterprise/salon/test',
    title: 'Hair FLUX',
    description: 'Upload a photo and type a prompt — raw FLUX hairstyle edit (test lab).',
    cta: 'Open Hair FLUX',
    hideLookHandoff: true,
  },
};

export function beautyAppPath(appBase, path) {
  const base = String(appBase || '').replace(/\/$/, '') || '/makeup';
  const raw = path == null || path === '' ? '/' : String(path);
  const p = raw.startsWith('/') ? raw : `/${raw}`;
  if (p === '/') return base;
  return `${base}${p}`;
}

/** Looks-style capture chrome: hero copy + Camera / Upload / Phone QR options. */
export function usesLooksCaptureChrome(appBase) {
  const base = String(appBase || '').replace(/\/$/, '');
  return (
    base.includes('/looks') ||
    base.includes('/makeup-studio-test') ||
    /\/makeup$/.test(base) ||
    base.includes('/hair-styling') ||
    base.includes('/hair-coloring')
  );
}

/**
 * Expand entitled module id/name list so legacy makeup-hair unlocks all three.
 * @param {Array<string|number|{id?:string|number,name?:string,slug?:string}>} entitled
 * @returns {Set<string>}
 */
export function expandEntitledBeautyModules(entitled) {
  const set = new Set();
  const list = Array.isArray(entitled) ? entitled : [];

  const add = (value) => {
    if (value == null || value === '') return;
    const s = String(value).trim();
    if (!s) return;
    set.add(s);
    set.add(s.toLowerCase());
  };

  list.forEach((item) => {
    if (item == null) return;
    if (typeof item === 'string' || typeof item === 'number') {
      add(item);
      return;
    }
    add(item.id);
    add(item.name);
    add(item.slug);
    add(item.moduleId);
    add(item.module_id);
  });

  const hasLegacy = LEGACY_MAKEUP_HAIR_MODULE_IDS.some(
    (id) => set.has(id) || set.has(String(id).toLowerCase()),
  );
  if (hasLegacy) {
    add(SAAS_MODULE_IDS.VIRTUAL_MAKEUP);
    add(SAAS_MODULE_IDS.HAIR_COLORING);
    add(SAAS_MODULE_IDS.HAIR_CUT);
  }

  return set;
}

/**
 * When no entitlement list is available, all beauty modules are enabled (open hub).
 * @param {Set<string>|null|undefined} entitledSet
 * @param {string|string[]} moduleId
 */
export function isBeautyModuleEntitled(entitledSet, moduleId) {
  if (!entitledSet || entitledSet.size === 0) return true;
  const ids = Array.isArray(moduleId) ? moduleId : [moduleId];
  return ids.some((moduleIdValue) => {
    const id = String(moduleIdValue || '');
    return (
      entitledSet.has(id) ||
      entitledSet.has(id.toLowerCase()) ||
      entitledSet.has(id.replace(/_/g, '-')) ||
      entitledSet.has(id.replace(/-/g, '_'))
    );
  });
}
